import { CommonModule, Location } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { AlertInfo } from 'src/app/models/alert-info';
import { SavedDataInfo } from 'src/app/models/form/saved-data-info';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { IntroducersService } from 'src/app/services/api/introducers.service';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { FormService } from 'src/app/services/form.service';
import { ValidatorService } from 'src/app/services/validator.service';
import { IntroducerCommissionComponent } from '../introducer-commission/introducer-commission.component';
import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';

@Component({
  selector: 'introducer-form',
  imports: [CommonModule, NgbAlert, InputFieldComponent, ReactiveFormsModule, IntroducerCommissionComponent, LoaderComponent],
  templateUrl: './introducer-form.component.html',
  styleUrl: './introducer-form.component.scss',
})
export class IntroducerFormComponent {
  @Input() introducer?: any;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();
  form!: FormGroup;
  commissions: any[] = [];
  contractFile: File | null = null;
  existingContract: any | null = null;
  isLoading: boolean = false;
  isLoadingCommissions: boolean = false;

  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  constructor(
    private validatorService: ValidatorService,
    private formService: FormService,
    private alertNotificationService: AlertNotificationService,
    private introducersService: IntroducersService,
    private confirmationDialogService: ConfirmationDialogService,
    private location: Location,
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup({
      name: new FormControl('', Validators.required),
      address: new FormControl(''),
      zipCode: new FormControl(''),
      city: new FormControl(''),
      vat: new FormControl(''),
      contractStartTime: new FormControl('', Validators.required),
    });
    if (this.introducer) {
      this.patchForm(this.introducer);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['introducer'] && changes['introducer'].currentValue && this.form) {
      this.patchForm(changes['introducer'].currentValue);
    }
  }

  private patchForm(i: any): void {
    this.form.patchValue({
      name: i.name,
      address: i.address,
      zipCode: i.zipCode,
      city: i.city,
      vat: i.vat,
      contractStartTime: i.contractStartTime ? i.contractStartTime.substring(0, 10) : '',
    });
    this.existingContract = i.contracts?.[0] ?? null;
    if (i.introducerId) {
      this.loadCommissions(i.introducerId);
    }
  }

  onContractSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      this.contractFile = null;
      return;
    }
    this.contractFile = input.files[0];
    this.existingContract = null;
  }

  confirmRemoveContract(): void {
    this.confirmationDialogService.open(
      'Are you sure you want to remove this contract?',
      () => this.removeContract()
    );
  }

  removeContract(): void {
    if (this.hasExistingContract && this.introducer?.introducerId) {
      this.isLoading = true;
      this.introducersService.deleteContract(this.introducer.introducerId).subscribe({
        next: () => {
          this.isLoading = false;
          this.contractFile = null;
          this.existingContract = null;
          this.showAlertInfo('success', 'Contract removed successfully');
        },
        error: (error: any) => {
          this.isLoading = false;
          this.showAlertInfo('danger', error?.error?.error?.message || 'Error removing contract');
        },
      });
      return;
    }
    this.contractFile = null;
    this.existingContract = null;
  }

  viewContract(): void {
    if (!this.existingContract?.url) return;
    this.introducersService.GET(this.existingContract.url).subscribe({
      next: (response: any) => {
        window.open(response.data.attachment.publicUrl, '_blank');
      },
    });
  }

  get activeContractName(): string | null {
    return this.existingContract?.fileNameOriginal ?? this.contractFile?.name ?? null;
  }

  get hasExistingContract(): boolean {
    return !!this.existingContract;
  }

  get hasPendingContract(): boolean {
    return !!this.contractFile;
  }

  showAlertInfo(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => { this.alertInfo.show = false; }, 3000);
  }

  updateValidationErrors(validationErrors: any): void {
    this.formService.resetErrors(this.form);
    this.formService.setValidationErrors(this.form, validationErrors);
  }

  submitFormSuccess(response: any): void {
    this.alertNotificationService.success('Operation completed successfully');
    this.showAlertInfo('success', 'Operation completed successfully');
    this.onSavedData.emit({ success: true, response });
  }

  submitFormError(error: any): void {
    if (error?.error?.error?.description === 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    } else {
      this.showAlertInfo('danger', error?.error?.error?.message || 'An error occurred');
    }
    this.onSavedData.emit({ success: false, error });
  }

  submitForm(): void {
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key)?.markAsTouched();
      this.form.get(key)?.updateValueAndValidity();
    });

    if (!this.form.valid) {
      return;
    }

    const formData = new FormData();

    const formValue = this.form.value;
    const data = {
      ...formValue,
      contractStartTime: formValue.contractStartTime
        ? `${formValue.contractStartTime}T00:00:00Z`
        : null,
    };
    formData.append('data', new Blob([JSON.stringify(data)], { type: 'application/json' }));

    if (this.contractFile) {
      formData.append('fileContract', this.contractFile);
    }

    this.alertInfo.show = false;
    this.formService.resetErrors(this.form);
    this.isLoading = true;

    const serviceMethod = this.introducer
      ? this.introducersService.update(this.introducer.introducerId, formData)
      : this.introducersService.create(formData);

    serviceMethod.pipe(
      switchMap((response: any) => {
        const savedId = response?.data?.introducer?.introducerId ?? this.introducer?.introducerId;
        return this.syncCommissions(savedId).pipe(map(() => response));
      })
    ).subscribe({
      next: (response: any) => { this.isLoading = false; this.submitFormSuccess(response); },
      error: (error: any) => { this.isLoading = false; this.submitFormError(error); },
    });
  }

  private loadCommissions(introducerId: number): void {
    this.isLoadingCommissions = true;
    this.introducersService.listRevenues(introducerId).subscribe({
      next: (response: any) => {
        const commissions = (response.data?.introducerRevenues || []).map((c: any) => ({
          introducerRevenueId: c.introducerRevenueId,
          startDate: c.startDate ?? '',
          endDate: c.endDate ?? '',
          revenuePercent: c.revenuePercent ?? null,
        }));
        this.commissions = commissions;
        this.isLoadingCommissions = false;
      },
      error: () => {
        this.isLoadingCommissions = false;
        this.alertNotificationService.error('Error loading commissions');
      },
    });
  }

  private syncCommissions(introducerId: number): Observable<any> {
    const ops: Observable<any>[] = [];

    for (const c of this.commissions) {
      if (!c.introducerRevenueId) {
        ops.push(this.introducersService.createRevenues(introducerId, {
          startDate: c.startDate,
          endDate: c.endDate,
          revenuePercent: c.revenuePercent,
        }));
      } else {
        ops.push(this.introducersService.updateRevenues(introducerId, c.introducerRevenueId, {
          introducerRevenueId: c.introducerRevenueId,
          introducerId,
          startDate: c.startDate,
          endDate: c.endDate,
          revenuePercent: c.revenuePercent,
        }));
      }
    }

    return ops.length ? forkJoin(ops) : of(null);
  }

  goBack(): void {
    this.location.back();
  }

  getNameInput(): FormControl { return this.form.get('name') as FormControl; }
  getAddressInput(): FormControl { return this.form.get('address') as FormControl; }
  getZipCodeInput(): FormControl { return this.form.get('zipCode') as FormControl; }
  getCityInput(): FormControl { return this.form.get('city') as FormControl; }
  getVatInput(): FormControl { return this.form.get('vat') as FormControl; }
  getContractStartTimeInput(): FormControl { return this.form.get('contractStartTime') as FormControl; }
}
